import { subscribe, unsubscribe } from "./events";

// Legacy shells do not echo requestId. Keep exactly one operation eligible for
// those events, and briefly drain an id-less result after an ambiguous stop.
// Correlated v2 operations continue in parallel and never wait for this drain.
export const LEGACY_MIC_DRAIN_MS = 750;

const operations = new Map();
let legacyOwnerId = null;
let legacyQueue = [];
let drain = null;
let listening = false;

const promoteLegacyOwner = () => {
  legacyOwnerId = null;
  while (legacyQueue.length) {
    const requestId = legacyQueue.shift();
    if (operations.has(requestId)) {
      legacyOwnerId = requestId;
      break;
    }
  }
};

const removeListenersIfIdle = () => {
  if (!listening || operations.size || drain) return;
  unsubscribe("gotMicCheckVolume", onVolume);
  unsubscribe("gotMicCheckResult", onResult);
  unsubscribe("gotMicCheckStopped", onStopped);
  listening = false;
};

const operationFor = (detail) => {
  const requestId = detail?.requestId;
  if (requestId) return operations.get(requestId);
  if (drain || !legacyOwnerId) return undefined;
  return operations.get(legacyOwnerId);
};

function onVolume(event) {
  operationFor(event?.detail)?.onVolume?.(event.detail || {});
}

function onResult(event) {
  operationFor(event?.detail)?.onResult?.(event.detail || {});
}

function onStopped(event) {
  const detail = event?.detail || {};
  if (detail.requestId && drain?.requestId === detail.requestId) {
    clearTimeout(drain.timer);
    drain = null;
    promoteLegacyOwner();
    removeListenersIfIdle();
    return;
  }
  operationFor(detail)?.onStopped?.(detail);
}

const ensureListeners = () => {
  if (listening) return;
  subscribe("gotMicCheckVolume", onVolume);
  subscribe("gotMicCheckResult", onResult);
  subscribe("gotMicCheckStopped", onStopped);
  listening = true;
};

const removeOperation = (requestId, ambiguous) => {
  if (!operations.has(requestId)) return;
  operations.delete(requestId);
  legacyQueue = legacyQueue.filter((id) => id !== requestId);
  if (legacyOwnerId === requestId) {
    legacyOwnerId = null;
    if (ambiguous) {
      if (drain) clearTimeout(drain.timer);
      const timer = setTimeout(() => {
        if (drain?.requestId !== requestId) return;
        drain = null;
        promoteLegacyOwner();
        removeListenersIfIdle();
      }, LEGACY_MIC_DRAIN_MS);
      drain = { requestId, timer };
    } else {
      promoteLegacyOwner();
    }
  }
  removeListenersIfIdle();
};

export const registerMicCheckOperation = ({ requestId, onVolume, onResult, onStopped }) => {
  const operation = { requestId, onVolume, onResult, onStopped };
  operations.set(requestId, operation);
  if (!legacyOwnerId && !drain) legacyOwnerId = requestId;
  else legacyQueue.push(requestId);
  ensureListeners();
  return {
    complete: () => removeOperation(requestId, false),
    cancel: () => removeOperation(requestId, true),
  };
};

const aliases = {
  audio_detected: ["ok", "working", true],
  audioDetected: ["ok", "working", true],
  no_audio_detected: ["warning", "noAudio", false],
  noAudio: ["warning", "noAudio", false],
  permission_denied: ["error", "accessDenied", false],
  permissionDenied: ["error", "accessDenied", false],
  permission_blocked: ["error", "accessDenied", false],
  permissionBlocked: ["error", "accessDenied", false],
  accessDenied: ["error", "accessDenied", false],
  recording_busy: ["warning", "busy", false],
  recordingBusy: ["warning", "busy", false],
  busy: ["warning", "busy", false],
  device_unavailable: ["warning", "unavailable", false],
  deviceUnavailable: ["warning", "unavailable", false],
  noDevice: ["error", "noDevice", false],
  start_failed: ["warning", "checkFailed", false],
  startFailed: ["warning", "checkFailed", false],
  stop_failed: ["warning", "checkFailed", false],
  stopFailed: ["warning", "checkFailed", false],
  invalid_request: ["warning", "checkFailed", false],
  invalidRequest: ["warning", "checkFailed", false],
};

export const normalizeMicCheckResult = (detail = {}) => {
  const normalized = aliases[detail.reason];
  if (normalized) return { status: normalized[0], reason: normalized[1], audioDetected: normalized[2] };
  if (detail.status === "ok") return { status: "ok", reason: "working", audioDetected: Boolean(detail.audioDetected) };
  if (detail.status === "warning") return { status: "warning", reason: "noAudio", audioDetected: Boolean(detail.audioDetected) };
  if (detail.status === "error") return { status: "error", reason: "unavailable", audioDetected: false };
  return { status: "warning", reason: "checkFailed", audioDetected: false };
};

export const normalizeMicCheckStopped = (detail = {}) => ({
  status: "warning",
  reason: detail.reason === "not_current" || detail.reason === "notCurrent" ? "notCurrent" : "stopped",
  audioDetected: false,
});

export const resetMicCheckCoordinatorForTests = () => {
  if (drain) clearTimeout(drain.timer);
  drain = null;
  operations.clear();
  legacyQueue = [];
  legacyOwnerId = null;
  if (listening) {
    unsubscribe("gotMicCheckVolume", onVolume);
    unsubscribe("gotMicCheckResult", onResult);
    unsubscribe("gotMicCheckStopped", onStopped);
  }
  listening = false;
};
